import { IntervalLabel } from './intervals';

// Chord qualities, ordered roughly from most-common (top) to most-niche (bottom).
// Order matters: it informs the priority of variant suggestions and parser
// suffix matching (longest match wins regardless of order, but conceptually
// earlier entries are "primary" forms).
export type ChordQuality =
  // ── Tier 1: essentials ─────────────────────────────
  | 'maj' | 'min' | '5'
  | 'dom7' | 'min7' | 'maj7'
  | 'sus2' | 'sus4'
  | 'add9'
  // ── Tier 2: common extensions / colour chords ──────
  | '6' | 'min6'
  | '9' | 'maj9' | 'min9'
  | 'dim' | 'aug'
  // ── Tier 3: less common but used in pop/rock/jazz ──
  | 'dim7' | 'min7b5'
  | '13'
  // ── Tier 4: Guitar Chord Bible additions (data-backed) ──
  | 'dom7sus4' | 'dom7s5' | 'dom7b9' | 'dom7s9';

export interface Chord {
  id: string;
  root: string;
  quality: ChordQuality;
  displayName: string;
  intervals: IntervalLabel[];
}

// Interval formulas. b9/#9/#11 don't have dedicated labels in our system;
// they're encoded as enharmonic equivalents (b9 → b2, #9 → b3, #11 → b5).
export const CHORD_INTERVALS: Record<ChordQuality, IntervalLabel[]> = {
  maj:        ['1', '3', '5'],
  min:        ['1', 'b3', '5'],
  '5':        ['1', '5'],
  dom7:       ['1', '3', '5', 'b7'],
  min7:       ['1', 'b3', '5', 'b7'],
  maj7:       ['1', '3', '5', '7'],
  sus2:       ['1', '2', '5'],
  sus4:       ['1', '4', '5'],
  add9:       ['1', '3', '5', '2'],
  '6':        ['1', '3', '5', '6'],
  min6:       ['1', 'b3', '5', '6'],
  '9':        ['1', '3', '5', 'b7', '2'],
  maj9:       ['1', '3', '5', '7', '2'],
  min9:       ['1', 'b3', '5', 'b7', '2'],
  dim:        ['1', 'b3', 'b5'],
  aug:        ['1', '3', 'b6'],
  dim7:       ['1', 'b3', 'b5', '6'],
  min7b5:     ['1', 'b3', 'b5', 'b7'],
  '13':       ['1', '3', '5', 'b7', '6'],
  // ── Tier 4: Guitar Chord Bible additions (data-backed) ──
  // Altered tones map enharmonically: #5→b6, b9→b2, #9→b3.
  dom7sus4:   ['1', '4', '5', 'b7'],
  dom7s5:     ['1', '3', 'b6', 'b7'],
  dom7b9:     ['1', '3', '5', 'b7', 'b2'],
  dom7s9:     ['1', '3', '5', 'b7', 'b3'],
};

// Display suffix for each quality (appended to the root)
export const QUALITY_DISPLAY: Record<ChordQuality, string> = {
  maj:        '',
  min:        'm',
  '5':        '5',
  dom7:       '7',
  min7:       'm7',
  maj7:       'maj7',
  sus2:       'sus2',
  sus4:       'sus4',
  add9:       'add9',
  '6':        '6',
  min6:       'm6',
  '9':        '9',
  maj9:       'maj9',
  min9:       'm9',
  dim:        'dim',
  aug:        '+',
  dim7:       'dim7',
  min7b5:     'ø7',
  '13':       '13',
  // ── Tier 4: Guitar Chord Bible additions (data-backed) ──
  dom7sus4:   '7sus4',
  dom7s5:     '7#5',
  dom7b9:     '7b9',
  dom7s9:     '7#9',
};

export function buildChord(root: string, quality: ChordQuality): Chord {
  const suffix = QUALITY_DISPLAY[quality];
  const displayName = suffix === '' ? root : `${root}${suffix}`;
  const id = quality === 'maj' ? root : displayName;
  return {
    id,
    root,
    quality,
    displayName,
    intervals: CHORD_INTERVALS[quality],
  };
}

// Variant qualities offered as quick-swap chips per base quality.
// Ordered from most-musically-related to least.
export const CHORD_VARIANTS: Partial<Record<ChordQuality, ChordQuality[]>> = {
  maj:    ['maj7', 'dom7', '6', 'add9', 'sus2', 'sus4', 'maj9', '5'],
  min:    ['min7', 'min6', 'min9', 'sus2', 'sus4', '5'],
  '5':    ['maj', 'min', 'sus2', 'sus4'],
  dom7:   ['9', '13', 'dom7b9', 'dom7s9', 'dom7sus4', 'dom7s5', 'maj7', 'maj'],
  min7:   ['min9', 'min', 'min6', 'min7b5'],
  maj7:   ['maj9', 'dom7', 'maj', '6'],
  sus2:   ['maj', 'sus4', 'add9'],
  sus4:   ['maj', 'sus2'],
  add9:   ['maj', 'maj9', 'sus2'],
  '6':    ['maj', 'maj7', 'min6', '9'],
  min6:   ['min', 'min7', '6', 'min9'],
  '9':    ['dom7', '13'],
  maj9:   ['maj7', 'maj'],
  min9:   ['min7', 'min'],
  dim:    ['dim7', 'min7b5'],
  aug:    [],
  dim7:   ['dim', 'min7b5'],
  min7b5: ['min7', 'dim', 'dim7'],
  '13':   ['dom7', '9'],
  // ── Tier 4: Guitar Chord Bible additions (data-backed) ──
  dom7sus4:   ['dom7', 'sus4', '9'],
  dom7s5:     ['dom7', 'aug'],
  dom7b9:     ['dom7', 'dom7s9', '9'],
  dom7s9:     ['dom7', 'dom7b9', '13'],
};
